/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { FieldArray, Field, Form, Formik } from 'formik';
import { debounce } from "lodash";
import * as yup from 'yup';
import Icon from 'components/Icon';
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCollum,
  InlineLabel,
  DialogIcon,
  ViewText,
  RadioContainer,
  CalendarContainer,
  ButtonContainer,
  ButtonContent,
  LabelEdit
} from './styles';
import { GridColumnTitle, Label } from 'components/Form';
import CheckBox from "components/Form/CheckBox";
import Row from 'components/Grid/Row';
import Col from 'components/Grid/Col';
import ComboBox from 'components/Form/ComboBox3';
import InputField from 'components/Form/InputField';
import { FitinsurAPI, MessageCenter } from 'utils';
import Button from 'components/Button';
import { GlobalLoaderContext, GlobalAuthContext } from 'store/context';
import SearchField from 'components/Form/SearchField';
import ModalConfirmDelete from 'components/ModalConfirmDelete';

const TexelBranchInformation = ({ idPerson, columns, data, hasPermission, onSavePersonRelationship }) => {
  const formikRef = useRef(null);
  const inputRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);

  const [sortBy, setSortBy] = useState({ key: null, direction: null, });
  const [addPersonRelationship, setAddPersonRelationship] = useState(false);
  const [idPersonRelationship, setIdPersonRelationship] = useState(null);
  const [searchList, setSearchList] = useState([])
  const [validations, setValidations] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePersonModalOpen, setDeletePersonModalOpen] = useState(false);
  const [directors, setDirectors] = useState(null);
  const [approvers, setApprovers] = useState(null);
  const [idTail, setIdTail] = useState(null);
  const [load, setLoad] = useState(false);
  const [idGroupExclude, setIdGroupExclude] = useState(null);
  const [idPersonUsersExclude, setIdPersonUsersExclude] = useState([]);
  const [hasDealPending, setHasDealPending] = useState(null)
  const [itemInfo, setItemInfo] = useState({})
  
  const [initial, setInitial] = useState({
    personTailLastName: '',
    idPersonTail: '',
    newDirectorPerson: '',
    idNewDirector: '',
  });

  const setEditPersonRelationship = (personRelationship) => {
    formikRef.current.values.legalEntityRegNumber = personRelationship.relationshipContent.legalEntityRegNumber;
    formikRef.current.values.legalEntityVatNumber = personRelationship.relationshipContent.legalEntityVatNumber;
    formikRef.current.values.minApprovalNumber = personRelationship.relationshipContent.minApprovalNumber ? personRelationship.relationshipContent.minApprovalNumber : 0;
    formikRef.current.values.minReqApprovalNumber = personRelationship.relationshipContent.minReqApprovalNumber ? personRelationship.relationshipContent.minReqApprovalNumber : 0;
    formikRef.current.values.personTailLastName = personRelationship.personTailLastName;
    formikRef.current.values.idPersonTail = personRelationship.idPersonTail;
    
    if (personRelationship.relationshipContent.directors && personRelationship.relationshipContent.approvers) {
      for (let director of personRelationship.relationshipContent.directors) {
        for (let approver of personRelationship.relationshipContent.approvers) {
          if (director.idPersonUser === approver.ID_PERSON) {
            director.required = approver.required;
          }
        }
      }
    } else {
      personRelationship.relationshipContent.approvers = personRelationship.relationshipContent.directors.map((director) => {
        const app =
        {
          ID_PERSON: director.idPersonUser,
          required: false
        };

        return app;
      });
    }

    if (personRelationship?.relationshipContent?.directors?.length != personRelationship?.relationshipContent?.approvers?.length) {
      for (let director of personRelationship.relationshipContent.directors) {
        const newDirector = personRelationship.relationshipContent.approvers.find(approver => approver.ID_PERSON === director.idPersonUser);

        if (!newDirector) {
          const app =
          {
            ID_PERSON: director.idPersonUser,
            required: false
          };

          personRelationship.relationshipContent.approvers.push(app);
        }
      };

      personRelationship.relationshipContent.approvers = personRelationship?.relationshipContent?.approvers.filter(approver => personRelationship.relationshipContent.directors.find(director => approver.ID_PERSON === director.idPersonUser));
    }

    setApprovers(personRelationship.relationshipContent?.approvers);
    setDirectors(personRelationship.relationshipContent?.directors);
    setIdTail(personRelationship.relationshipContent?.directors[0]?.idPersonTail);
    getAffectedEnquiries(personRelationship.relationshipContent?.directors[0]?.idPersonTail)

    setAddPersonRelationship(true);
    setIdPersonRelationship(personRelationship.idPersonRelationship);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 100)
  }

  const resetForm = () => {
    loading.start();
    formikRef.current.values.legalEntityRegNumber = '';
    formikRef.current.values.legalEntityVatNumber = '';
    formikRef.current.values.personTailLastName = '';
    formikRef.current.values.idPersonTail = ''
    formikRef.current.values.minApprovalNumber = '';
    formikRef.current.values.minReqApprovalNumber = '';
    formikRef.current.values.newDirectorPerson = '';
    formikRef.current.values.idNewDirector = '';

    setApprovers([]);
    setDirectors([]);
    loading.stop();
  }

  const saveRelationship = async (body) => {
    try {
      let reqs = approvers ? approvers.filter((approver) => {
        if (approver.required) {
          return approver
        }
      }) : null

      if (reqs && reqs.length < formikRef.current.values.minReqApprovalNumber) {
        mc.error("Selected directors must be greater than or equal than Required Approvals Number");
        return;
      }
      
      loading.start();

      if(idPersonUsersExclude && idPersonUsersExclude.length) {
        for(const idPersonExclude of idPersonUsersExclude) {
          await deletePersonDirector(idGroupExclude, idPersonExclude);
        }
      }

      for(const director of ( directors ? directors : [] )) {
        if(director.draft) { 
          const body = {};

          body.idPersonDirector = director.idPersonUser;
          body.idPersonTail = idTail;

          const response = await saveDirectorPerson(body);

          if(response.ret_code !== 200){
            return;
          }
        }
      }

      body.idPersonHead = idPerson;
      body.idPersonRelationship = idPersonRelationship;
      body.idDomain = 379;
      body.relationshipContent = {
        legalEntityRegNumber: body.legalEntityRegNumber,
        legalEntityVatNumber: body.legalEntityVatNumber,
        minApprovalNumber: body.minApprovalNumber,
        minReqApprovalNumber: body.minReqApprovalNumber,
        approvers: approvers
      }

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestSavePersonRelationship(body).then(res => res.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      setIdPersonRelationship(null);
      setAddPersonRelationship(false);
      setIdPersonUsersExclude([]);
      setIdGroupExclude(null);
      resetForm();
      onSavePersonRelationship && onSavePersonRelationship();
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.\nError details: [" + err.message + "]");
    };
  }


  const saveDirectorPerson = async (body) => {
    try {
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestSavePersonDirector(body).then(res => res.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
      }
      
      return response;
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }


  const deletePersonDirector = async (idGroup, idPersonUser) => {
    try {
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestDeletePersonDirector(idGroup, idPersonUser).then(res => res.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }


  const deletePersonRelationship = async (idPersonRelationship) => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestDeletePersonRelationship(idPersonRelationship).then(res => res.json());
      setIdPersonRelationship(null);

      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      onSavePersonRelationship && onSavePersonRelationship();
    } catch (err) {
      setIdPersonRelationship(null);
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const searchCompanies = async (searchValue) => {
    try {
      fitinsur.setAuthToken(authContext.token);
      fitinsur.requestGetPersonsList(`?fullName=${searchValue}&idPersonRoot=${idPerson}&personType=35`).then((response) => response.json()).then((data) => {
        if (data.ret_code != 200) {
          return;
        }

        data.ret_value = data.ret_value?.map(v => ({ ...v, id: v.idPerson, personName: `${v.lastName}` }))
        setSearchList(data.ret_value);
      });
    } catch (err) {
      console.log("FETCH_FAILURE: ", err);
    }
  }

  const searchPersons = async (searchValue) => {
    try {
      fitinsur.setAuthToken(authContext.token);
      fitinsur.requestGetPersonsList(`?personName=${searchValue}&idPersonRoot=${idPerson}&personType=34`).then((response) => response.json()).then((data) => {
        if (data.ret_code != 200) {
          return;
        }

        data.ret_value = data.ret_value?.map(v => ({ ...v, id: v.idPerson, personName: `${v.firstName} ${v.lastName}` }))
        setSearchList(data.ret_value);
      });
    } catch (err) {
      console.log("FETCH_FAILURE: ", err);
    }
  }

  const getPerson = async (idPerson) => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestGetPersonsInfo(idPerson).then((res) => res.json());

      if (response.ret_code !== 200) mc.error(response.ret_msg_details);
      loading.stop();

    } catch (error) {
      console.log("Error fetch data: ", error);
      loading.stop();
      mc.error("An error happened, please try again.");
    }
  };

  const createValidation = () => {

    if (directors || !load) {
      let validations = yup.object().shape({
        idPersonTail: yup.string().required("* Required field"),
        minApprovalNumber: yup.mixed().required('* Required field').when("minReqApprovalNumber", (minReqApprovalNumber, minApprovalNumber) =>
          minApprovalNumber.test("greater than", "* Must be greater than or equal than Required Approvals Number", approvals => approvals >= minReqApprovalNumber)),

      });
      setValidations(validations);
      setLoad(true)
      return validations

    } else {
      let validations = yup.object().shape({
        idPersonTail: yup.string().required("* Required field"),
      });
      setValidations(validations);
      setLoad(true)
      return validations
    }

  }

  const setApproversRequired = async (director, e, setFieldValue) => {
    try {
      loading.start();

      const { checked } = e ? e.target : false;

      setTimeout(() => checkApprover(director, e, checked, setFieldValue), 10);

    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  };

  function checkApprover(director, e, checked, setFieldValue) {
    const approver = {
      ID_PERSON: director.idPersonUser,
      required: checked
    };

    for (let dir of directors) {
      if (dir.idPersonUser === director.idPersonUser) {
        dir.required = checked;
      }
    }

    if (approvers) {
      for (let app of approvers) {
        if (app.ID_PERSON === approver.ID_PERSON) {
          app.required = approver.required;
        }
      }
    }

    loading.stop();
  }

  const limitNumber = (event, setFieldValue) => {
    let value;
    if (event < formikRef.current.values.minReqApprovalNumber) {
      value = formikRef.current.values.minReqApprovalNumber;
    } else if (approvers && approvers.length) {
      value = Math.max(0, Math.min(approvers?.length, Number(event)));
    } else {
      value = Math.max(0, Math.min(directors?.length, Number(event)));
    }

    setFieldValue('minApprovalNumber', value);
  };

  const limitReqNumber = (event, setFieldValue) => {
    let value;

    let reqs = approvers.filter((approver) => {
      if (approver.required) {
        return approver
      }
    })

    if (reqs && reqs.length) {
      value = Math.max(0, Math.min(reqs?.length, Number(event)));
    } else if (event > formikRef.current.values.minApprovalNumber) {
      value = formikRef.current.values.minApprovalNumber;
    } else if (approvers && approvers.length) {
      value = Math.max(0, Math.min(approvers?.length, Number(event)));
    } else {
      value = Math.max(0, Math.min(directors?.length, Number(event)));
    }

    setFieldValue('minReqApprovalNumber', value);
  };

  useEffect(() => {
    createValidation();
    getAffectedEnquiries();
  }, []);

  const debouncedChangeHandler = useCallback(
    debounce(searchCompanies, 300)
    , []);


  const debouncedChangeHandlerPersons = useCallback(
    debounce(searchPersons, 300)
    , []);
  
  const getDirectors = idPerson => {
    const findedPerson = data.find(d => d.idPersonTail === idPerson);

    if (findedPerson) {
      const newDirectors = findedPerson.relationshipContent?.directors.map(director => {
        return {
          ...director,
          required: false
        }
      })
      setDirectors(newDirectors)
    }
  }

  const getAffectedEnquiries = (tail) => {  
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestListEnquiries(tail).then(x => x.json())
      .then(resp => {
        if (resp.ret_code === 200) {
          if(resp.ret_value.length) setHasDealPending(true);
        }
      }).catch(() => {
        mc.error("There was an error while fetching your data");
      });
  } 

  const handleDeletePerson = () => { 
    const directorsFiltered = directors.filter(director => director.idPersonUser !== itemInfo?.idPersonUser);
    const directorsToExclude = directors.find(director => director.idPersonUser === itemInfo?.idPersonUser)?.idPersonUser;
    const approversFiltered = approvers.filter(approver => approver.ID_PERSON !== itemInfo?.idPersonUser);

    setApprovers(approversFiltered)
    setDirectors(directorsFiltered)
    setIdPersonUsersExclude(id => [...id, directorsToExclude]);
    setIdGroupExclude(itemInfo?.idGroup)    
  }
  
  return (
    <Formik
      innerRef={formikRef}
      initialValues={initial}
      enableReinitialize={true}
      onSubmit={(values) => saveRelationship(values)}
      validationSchema={validations}
    >
      {({ setFieldValue, values }) => (
        <>
          <ModalConfirmDelete show={deleteModalOpen} onModalClose={() => { setDeleteModalOpen(false); setIdPersonRelationship(null); }} onConfirm={() => deletePersonRelationship(idPersonRelationship)} />
          <ModalConfirmDelete
            deleteMessage={hasDealPending ? 'Any removed director has deal(s) pending signature, would you like to proceed with the removal?.' : 'Are you sure you want to delete this record?'}
            show={deletePersonModalOpen} 
            onModalClose={() => { setDeletePersonModalOpen(false);}} 
            onConfirm={() => handleDeletePerson()} />
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column, index) =>
                  <TableHeader right={column.right} key={index} minWidth={column.minWidth} maxWidth={column.maxWidth}>
                    <GridColumnTitle style={{ cursor: column.value ? 'pointer' : 'not-allowed' }}>
                      {column.label}
                    </GridColumnTitle>
                    {column.value && sortBy.key === column.value && sortBy.direction === "asc" ?
                      <Icon name={"arrowup"} /> :
                      column.value && sortBy.key === column.value && sortBy.direction === "desc" ?
                        <Icon name={"arrowdown"} /> :
                        ''}
                  </TableHeader>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              <FieldArray
                name="management"
                render={() => data.map((item, index) => (
                  <TableRow key={index} >
                    <TableCollum>
                      <InlineLabel>
                        Texel Legal Entity
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.personTailLastName}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Legal Entity Register Number
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.legalEntityRegNumber || ""}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Legal Entity VAT Number
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.legalEntityVatNumber || ""}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum right>
                      <InlineLabel>
                        ACTION
                      </InlineLabel>
                      <Label>
                        <Icon
                          name="edit"
                          style={{ alignSelf: "center", cursor: "pointer" }}
                          onClick={() => setEditPersonRelationship(item)}
                        />
                        <Icon
                          name="trash"
                          style={{ alignSelf: "center", cursor: "pointer" }}
                          onClick={() => {
                            setIdPersonRelationship(item.idPersonRelationship);
                            setDeleteModalOpen(true);
                            setAddPersonRelationship(false);
                            resetForm();
                          }} />
                      </Label>
                    </TableCollum>
                  </TableRow>
                ))
                }
              />
            </TableBody>
          </Table>

          {!addPersonRelationship && <Row>
            <Col lg={6} md={6} sm={6} xs={12}></Col>
            <Col lg={6} md={6} sm={6} xs={12}>
              <ViewText style={{ marginTop: '15px' }}>
                <Icon name="plus" onClick={() => setAddPersonRelationship(true)} style={{ cursor: 'pointer' }} />
              </ViewText>
            </Col>
          </Row>}

          {addPersonRelationship && <Form>
            <div style={{ marginTop: 40, marginBottom: 10 }}>
              <Row>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="personTailLastName"
                    label="Legal Entity"
                    isRequired
                    component={SearchField}
                    disabled={idPersonRelationship}
                    searchList={searchList}
                    value={values.personTailLastName && values.personTailLastName}
                    onChange={debouncedChangeHandler}
                    onFocus={() => {
                      setSearchList([]);
                      searchCompanies("");
                    }}
                    canGenerate={false}
                    onSearchSelect={(selectedValue) => {
                      getPerson(selectedValue.idPerson)
                      setFieldValue(`personTailLastName`, selectedValue.lastName);
                      setFieldValue(`idPersonTail`, selectedValue.idPerson);
                      setSearchList([]);
                      getDirectors(selectedValue.idPerson)
                    }}
                  />
                </Col>
              </Row>

              <Row>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="legalEntityRegNumber"
                    label="Legal Entity Register Number"
                    component={InputField}
                  />
                </Col>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="legalEntityVatNumber"
                    label="Legal Entity VAT Number"
                    component={InputField}
                  />
                </Col>
              </Row>
              <Row>
                <Col lg={6} md={6} sm={6} xs={12}></Col>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <ViewText>
                    <Icon name="plus" />
                  </ViewText>
                </Col>
              </Row>
              
              {directors && directors.length > 0 && (
                <>
                  <Row>
                    <Col lg={6} md={6} sm={6} xs={12}>
                      <Field
                        name="minApprovalNumber"
                        label="Approvals Number"
                        component={InputField}
                        values={values.minApprovalNumber}
                        type="Number"
                        onChange={(e) => limitNumber(e, setFieldValue)}
                      />
                    </Col>
                    <Col lg={6} md={6} sm={6} xs={12}>
                      <Field
                        name="minReqApprovalNumber"
                        label="Required Approvals Number"
                        component={InputField}
                        values={values.minReqApprovalNumber}
                        type="Number"
                        onChange={(e) => limitReqNumber(e, setFieldValue)}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col lg={6} md={6} sm={6} xs={10}>
                      <Field
                        name="newDirectorPerson"
                        label="Director"
                        component={SearchField}
                        searchList={searchList}
                        value={values.newDirectorPerson && values.newDirectorPerson}
                        onChange={debouncedChangeHandlerPersons}
                        onFocus={() => {
                          setSearchList([]);
                          searchPersons("");
                        }}
                        canGenerate={false}
                        onSearchSelect={value => {
                          setFieldValue(`newDirectorPerson`, `${value.firstName} ${value.lastName}`);
                          setFieldValue(`firstNameDirector`, value.firstName);
                          setFieldValue(`lastNameDirector`, value.lastName);
                          setFieldValue(`idNewDirector`, value.idPerson);
                          setSearchList([]);
                      }}
                      />
                    </Col>
                    <Col lg={6} md={6} sm={6} xs={2}>
                      <ViewText left style={{ marginTop: '25px' }}>
                        <Icon name="plus" onClick={() => {
                          const findedDirector = directors.find(director => director.idPersonUser === values.idNewDirector);

                          if(findedDirector) {
                            setFieldValue('newDirectorPerson', '');
                            return mc.error('The selected person already a director!');
                          }

                          setDirectors(director => [...director, {                           
                            firstNmUser: values.firstNameDirector,
                            lastNmUser: values.lastNameDirector,
                            idPersonTail: idTail,
                            idPersonUser: values.idNewDirector,
                            draft: true
                          }]);
                          setApprovers(approver => [...approver, {
                            ID_PERSON: values.idNewDirector,
                            required: false,
                          }]);
                          formikRef.current.values.newDirectorPerson = '';
                        }}
                        style={{ cursor: 'pointer' }} /> 
                      </ViewText>
                    </Col>
                  </Row>
                </>
              )}

              {directors && directors.length > 0 &&
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>
                        Director
                      </TableHeader>
                      <TableHeader>
                        Required
                      </TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <FieldArray
                      name="management"
                      render={() => directors && directors.map((item, index) => (
                        <TableRow key={index}>
                          <TableCollum>
                            <InlineLabel>
                              DIRECTOR
                            </InlineLabel>
                            <LabelEdit fontSize={12}>
                              {item.firstNmUser + ' ' + item.lastNmUser}
                            </LabelEdit>
                          </TableCollum>

                          <TableCollum>
                            <InlineLabel>
                              REQUIRED
                            </InlineLabel>
                            <LabelEdit fontSize={12}>
                              <CheckBox
                                onChange={(e) => { setApproversRequired(item, e, setFieldValue); }}
                                checked={item.required}
                              />
                            </LabelEdit>
                          </TableCollum>

                          <TableCollum>
                            <LabelEdit fontSize={12}>
                            <Icon
                              name="trash"
                              style={{ alignSelf: "center", cursor: "pointer" }}
                              onClick={() => {
                                if(directors && directors.length - 1 === 0) return mc.error("It's not allowed delete all directors!");

                                if(item.draft) {
                                  const directorsFiltered = directors.filter(director => director.idPersonUser !== item?.idPersonUser);
                                  const approversFiltered = approvers.filter(approver => approver.ID_PERSON !== item?.idPersonUser);

                                  setApprovers(approversFiltered)
                                  setDirectors(directorsFiltered);
                                } else { 
                                  setItemInfo(item);
                                  setDeletePersonModalOpen(true);
                                }
                              }}
                            />
                            </LabelEdit>
                          </TableCollum>
                        </TableRow>
                      ))
                      }
                    />
                  </TableBody>
                </Table>}

              <ButtonContainer>
                <ButtonContainer style={{ justifyContent: 'flex-start', alignItems: 'center', paddingBottom: '10px' }} />
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    third
                    onClick={() => {
                      setIdPersonRelationship(null);
                      setAddPersonRelationship(false);
                      setIdPersonUsersExclude([]);
                      setIdGroupExclude(false);
                      resetForm();
                    }}
                  >
                    CANCEL
                  </Button>
                </ButtonContent>
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    type="submit"
                    primary
                    onClick={() => {
                      createValidation().validate(values).catch((e) => {
                        console.log('validation error', e)
                        mc.error('Please fill in all required fields.')
                      })
                    }}
                    id="btnSubmit">
                    {idPersonRelationship ? "SAVE" : "ADD"}
                  </Button>
                </ButtonContent>
              </ButtonContainer>
            </div>
          </Form>}
        </>
      )}
    </Formik>
  )
}

export default TexelBranchInformation;